import {compact} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useMemo} from 'react';

import {assert} from 'Asserts';
import {BenchlingExpressionFunction} from 'benchling-expressions/BenchlingExpressionTypes';
import {
  AndFunctionSignature,
  BenchlingFunctionSignature,
  getDisplayNameFromFunctionSignature,
  getLabelFromFunctionSignature,
} from 'benchling-expressions/BenchlingFunctionSignatures';
import {AnalyticsMetadataForContext} from 'constants/AnalyticsConstants';
import {ProcessGraphBuilderNode} from 'processes/graph-builder-processes/validation/ProcessGraphBuilderConstants';
import AddConditionButton from 'processes/modals/router-config/AddConditionButton';
import ConditionOptionsButton from 'processes/modals/router-config/ConditionOptionsButton';
import LogicalFunctionConfig from 'processes/modals/router-config/LogicalFunctionConfig';
import {
  BOOLEAN_OPERATORS,
  BOOLEAN_OPERATORS_BY_FUNCNAME,
} from 'processes/modals/router-config/RouterConfigConstants';
import {expressionFunctionIsBoolean} from 'processes/modals/router-config/RouterConfigUtils';
import {Schema} from 'schemas/SchemaConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import {GRAY_LIGHTER, WHITE, GRAY_LIGHT} from 'ui-library/Colors';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import Menu from 'ui-library/menus/Menu';
import {MenuHeight, MenuWidth} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';
import {DeepPartial} from 'util/Types';

import {ProcessOutputSchemaGraphQL} from '../../../../graphql-fragments/processes/schemas/__generated/ProcessOutputSchemaGraphQL';
import {ProcessTaskSchemaGraphQL} from '../../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaGraphQL';
import DeleteConditionButton from './DeleteConditionButton';

interface MultipleConditionConfigErrors {
  isNextNodeDuplicate: boolean;
}

export enum ConditionConfigColor {
  GRAY,
  WHITE,
}

export interface MultipleConditionConfigProps {
  routerNodeClientId: string;
  flowchartClientId: string;
  onIsRouterFunctionNameBeingEdited: (isBeingEdited: boolean) => void;
  expression: DeepPartial<BenchlingExpressionFunction>;
  setExpression: (newExpression: DeepPartial<BenchlingExpressionFunction>) => void;
  inputNode: ProcessGraphBuilderNode;
  flowchartTaskSchema: Schema;
  immediateChildNodes: Array<ProcessGraphBuilderNode>;
  inputSchema: ProcessTaskSchemaGraphQL | ProcessOutputSchemaGraphQL;
  didInitialFlowchartHaveChainedRouters: boolean;
  doesCurrentFlowchartHaveChainedRouters: boolean;
  analyticsMetadata: AnalyticsMetadataForContext;
  dataTestAttrs?: DataTestAttrs;
  errors: MultipleConditionConfigErrors;
  recursiveDepth: number; // Integer representing the recursive depth of the component
}

const ALLOWED_OPERATORS = BOOLEAN_OPERATORS;
export const DEFAULT_OPERATOR = AndFunctionSignature;
const BOOLEAN_DROPDOWN_TOOLTIP_TEXT = 'You can change this option when you add more conditions.';

export default function MultipleConditionConfig(props: MultipleConditionConfigProps): JSX.Element {
  /*
  Multi-condition container that can contain multiple subexpression components and joins them with
  a boolean expression.
  */
  const {operatorDropdownName, operatorSignature, expressions, expressionIndexToIsBoolean} = useMemo(() => {
    assert(expressionFunctionIsBoolean(props.expression), 'Expected expression to be a boolean');
    const operatorSignature = getFunctionSignatureFromName(props.expression?.name);
    const operatorDropdownName = getDisplayNameFromFunctionSignature(operatorSignature);

    const expressions = getSubExpressions(props.expression);
    assert(expressions.length > 0, 'Expected at least one expression');
    const expressionIndexToIsBoolean = Object.fromEntries(
      expressions.map((expr, idx) => [idx, expressionFunctionIsBoolean(expr)])
    );

    return {
      operatorDropdownName,
      operatorSignature,
      expressions,
      expressionIndexToIsBoolean,
    };
  }, [props.expression]);

  const {color, operatorPrefix, operatorSuffix} = useMemo(() => {
    switch (props.recursiveDepth) {
      case 0:
        return {
          color: ConditionConfigColor.GRAY,
          operatorPrefix: 'When',
          operatorSuffix: 'the following are true...',
        };
      case 1:
        return {
          color: ConditionConfigColor.WHITE,
          operatorPrefix: '',
          operatorSuffix: 'the following are true...',
        };
      default:
        throw new Error('Invalid recursive depth');
    }
  }, [props.recursiveDepth]);

  function rowPrefixTextGenerator(idx: number): string {
    if (idx === 0) {
      return 'If';
    }
    const label = getLabelFromFunctionSignature(operatorSignature);
    assert(label != null, 'Expected label to not be null');
    return label;
  }

  function getSubExpressions(
    expression: DeepPartial<BenchlingExpressionFunction>
  ): Array<DeepPartial<BenchlingExpressionFunction>> {
    assert(expression != null, 'Expected expression to not be null');
    assert(expression.parameters !== undefined, 'Expected parameters to be defined');
    return compact(
      expression.parameters.map((param) => param?.expression as DeepPartial<BenchlingExpressionFunction>)
    );
  }

  function getFunctionSignatureFromName(name: string | undefined): BenchlingFunctionSignature {
    assert(name !== undefined, 'Expected name to be defined');
    const signature = BOOLEAN_OPERATORS_BY_FUNCNAME[name];
    assert(signature !== undefined, `Expected signature for ${name}`);
    return signature;
  }

  function setSubExpression(newExpression: DeepPartial<BenchlingExpressionFunction>, idx: number): void {
    const newParams = expressions.map((expr, i) => ({
      name: DEFAULT_OPERATOR.parameters[0].spreadParameterName,
      expression: i === idx ? newExpression : expr,
    }));
    props.setExpression({
      ...props.expression,
      name: newParams.length === 1 ? DEFAULT_OPERATOR.functionName : props.expression.name,
      parameters: newParams,
    });
  }

  return (
    <div
      className={classNames(
        css(styles.outerAlignment),
        color === ConditionConfigColor.GRAY && css(styles.grayBackground),
        color === ConditionConfigColor.WHITE && css(styles.whiteBackground),
        props.recursiveDepth > 0 && css(styles.addSpacing)
      )}
      {...getTestProps(props.dataTestAttrs)}
    >
      <div className={css(styles.operatorRow)}>
        {operatorPrefix}
        <Tooltip isDisabled={expressions.length > 1} text={BOOLEAN_DROPDOWN_TOOLTIP_TEXT}>
          <SelectDropdown
            className={css(styles.dropdown)}
            displayName={operatorDropdownName}
            disabled={expressions.length < 2}
            shouldEllipsize={false}
            menu={
              <Menu height={MenuHeight.MEDIUM} width={MenuWidth.FULL}>
                {ALLOWED_OPERATORS
                  ? ALLOWED_OPERATORS.map((operator: BenchlingFunctionSignature, index) => (
                      <Option
                        key={operator.functionName}
                        value={operator.functionName}
                        onSelect={() => {
                          assert(
                            expressionFunctionIsBoolean(props.expression),
                            'Expected expression to already be a boolean'
                          );
                          props.setExpression({
                            ...props.expression,
                            name: operator.functionName,
                          });
                        }}
                      >
                        {getDisplayNameFromFunctionSignature(operator)}
                      </Option>
                    ))
                  : null}
              </Menu>
            }
            dataTestAttrs={{
              component: 'MultipleConditionConfig',
              element: 'booleanDropdown',
              key: `${props.recursiveDepth}`,
            }}
          />
        </Tooltip>
        {operatorSuffix}
      </div>
      <table>
        <tbody>
          {expressions.map((expr, idx) => (
            <tr key={idx} className={css(styles.row)}>
              <td className={css(styles.prefix)}>{rowPrefixTextGenerator(idx)}</td>
              <td className={css(styles.inlineDelete)}>
                {expressionIndexToIsBoolean[idx] ? (
                  <MultipleConditionConfig
                    routerNodeClientId={props.routerNodeClientId}
                    flowchartClientId={props.flowchartClientId}
                    expression={expr}
                    setExpression={(expression) => {
                      setSubExpression(expression, idx);
                    }}
                    onIsRouterFunctionNameBeingEdited={props.onIsRouterFunctionNameBeingEdited}
                    inputNode={props.inputNode}
                    inputSchema={props.inputSchema}
                    immediateChildNodes={props.immediateChildNodes}
                    flowchartTaskSchema={props.flowchartTaskSchema}
                    analyticsMetadata={props.analyticsMetadata}
                    dataTestAttrs={{
                      component: 'MultipleConditionConfig',
                      element: 'MultipleConditionConfig',
                      key: `${props.recursiveDepth + 1}.${idx}`,
                    }}
                    errors={props.errors}
                    recursiveDepth={props.recursiveDepth + 1}
                    didInitialFlowchartHaveChainedRouters={props.didInitialFlowchartHaveChainedRouters}
                    doesCurrentFlowchartHaveChainedRouters={props.doesCurrentFlowchartHaveChainedRouters}
                  />
                ) : (
                  <LogicalFunctionConfig
                    routerNodeClientId={props.routerNodeClientId}
                    flowchartClientId={props.flowchartClientId}
                    expression={expr}
                    setExpression={(expression) => {
                      setSubExpression(expression, idx);
                    }}
                    inputNode={props.inputNode}
                    inputSchema={props.inputSchema}
                    flowchartTaskSchema={props.flowchartTaskSchema}
                    analyticsMetadata={props.analyticsMetadata}
                    dataTestAttrs={{
                      component: 'MultipleConditionConfig',
                      element: 'LogicalFunctionConfig',
                      key: `${idx}`,
                    }}
                    errors={props.errors}
                  />
                )}
                {props.recursiveDepth > 0 || expressionIndexToIsBoolean[idx] ? (
                  <DeleteConditionButton
                    idx={idx}
                    expression={props.expression}
                    setExpression={props.setExpression}
                    defaultOperator={DEFAULT_OPERATOR}
                    recursiveDepth={props.recursiveDepth}
                    isExpressionBoolean={expressionIndexToIsBoolean[idx]}
                    dataTestAttrs={{
                      component: 'MultipleConditionConfig',
                      element: 'deleteConditionButton',
                      key: `${props.recursiveDepth}.${idx}`,
                    }}
                  />
                ) : (
                  <ConditionOptionsButton
                    idx={idx}
                    expression={props.expression}
                    setExpression={props.setExpression}
                    isExpressionBoolean={expressionIndexToIsBoolean[idx]}
                    defaultOperator={DEFAULT_OPERATOR}
                    recursiveDepth={props.recursiveDepth}
                    didInitialFlowchartHaveChainedRouters={props.didInitialFlowchartHaveChainedRouters}
                    doesCurrentFlowchartHaveChainedRouters={props.doesCurrentFlowchartHaveChainedRouters}
                    dataTestAttrs={{
                      component: 'MultipleConditionConfig',
                      element: 'conditionOptionsButton',
                      key: `${props.recursiveDepth}.${idx}`,
                    }}
                  />
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <AddConditionButton
        dataTestAttrs={{
          component: 'MultipleConditionConfig',
          element: 'addConditionButton',
          key: `${props.recursiveDepth}`,
        }}
        expression={props.expression}
        setExpression={props.setExpression}
        recursiveDepth={props.recursiveDepth}
        defaultOperator={DEFAULT_OPERATOR}
        didInitialFlowchartHaveChainedRouters={props.didInitialFlowchartHaveChainedRouters}
        doesCurrentFlowchartHaveChainedRouters={props.doesCurrentFlowchartHaveChainedRouters}
      />
    </div>
  );
}

const styles = StyleSheet.create({
  outerAlignment: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 16,
    paddingBlock: 16,
  },
  addSpacing: {
    padding: '8px',
    marginTop: '4px',
    marginBottom: '4px',
  },
  prefix: {
    textAlign: 'right',
    padding: '4px 8px 4px 0',
    width: '28px',
  },
  grayBackground: {
    backgroundColor: GRAY_LIGHTER,
  },
  whiteBackground: {
    backgroundColor: WHITE,
    border: '1px solid',
    borderColor: GRAY_LIGHT,
  },
  operatorRow: {
    display: 'inline-flex',
    alignItems: 'baseline',
    gap: 8,
  },
  dropdown: {
    width: 75,
  },
  row: {
    verticalAlign: 'baseline',
  },

  inlineDelete: {
    display: 'flex',
    flexDirection: 'row',
  },
});
